import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Search, Filter, ChevronRight, Loader2, 
  User, Mail, Phone, Calendar, Activity, Tag,
  TrendingUp, Eye, MousePointer, ShoppingCart, 
  Download, ArrowUp, ArrowDown, MoreHorizontal,
  X, Check, Edit2, Trash2, Plus, RefreshCw,
  Thermometer, Star, Clock, ExternalLink,
  UserCheck, UserPlus, UserX, Zap, Target
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import api from '../../lib/api';
import { toast } from 'sonner';

// ============================================
// LIFECYCLE ICONS & COLORS
// ============================================

const LIFECYCLE_CONFIG = {
  visitor: { icon: Eye, color: 'bg-gray-100 text-gray-600', label: 'Visitante' },
  subscriber: { icon: Mail, color: 'bg-blue-100 text-blue-600', label: 'Suscriptor' },
  lead: { icon: Target, color: 'bg-amber-100 text-amber-600', label: 'Lead' },
  customer: { icon: ShoppingCart, color: 'bg-green-100 text-green-600', label: 'Cliente' },
  student: { icon: UserCheck, color: 'bg-purple-100 text-purple-600', label: 'Estudiante' }
};

const TEMPERATURE_CONFIG = {
  hot: { color: 'bg-red-500', label: 'Caliente', emoji: '🔥' },
  warm: { color: 'bg-amber-500', label: 'Tibio', emoji: '☀️' },
  cold: { color: 'bg-blue-500', label: 'Frío', emoji: '❄️' }
};

// ============================================
// CONTACT ROW COMPONENT
// ============================================

const ContactRow = ({ contact, onClick, isSelected }) => {
  const lifecycle = LIFECYCLE_CONFIG[contact.lifecycle_stage] || LIFECYCLE_CONFIG.visitor;
  const temp = TEMPERATURE_CONFIG[contact.lead_temperature] || TEMPERATURE_CONFIG.cold;
  const LifecycleIcon = lifecycle.icon;
  
  return (
    <tr 
      className={`hover:bg-gray-50 cursor-pointer transition-colors ${isSelected ? 'bg-amber-50' : ''}`}
      onClick={() => onClick(contact)}
      data-testid={`contact-row-${contact.id}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${lifecycle.color}`}>
            <LifecycleIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="font-medium text-gray-900">
              {contact.first_name || contact.email.split('@')[0]}
              {contact.last_name && ` ${contact.last_name}`}
            </div>
            <div className="text-sm text-gray-500">{contact.email}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline" className={lifecycle.color}>
          {lifecycle.label}
        </Badge>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${temp.color}`} title={temp.label} />
          <span className="text-sm font-medium">{contact.lead_score}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {contact.tag_names?.slice(0, 3).map((tag, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
          {contact.tag_names?.length > 3 && (
            <Badge variant="secondary" className="text-xs">
              +{contact.tag_names.length - 3}
            </Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {contact.source || 'N/A'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {contact.last_activity_at 
          ? new Date(contact.last_activity_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
          : 'N/A'
        }
      </td>
      <td className="px-4 py-3">
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </td>
    </tr>
  );
};

// ============================================
// CONTACT DETAIL PANEL (360 VIEW)
// ============================================

const ContactDetailPanel = ({ contact, onClose, onUpdate, onDelete }) => {
  const [loading, setLoading] = useState(true);
  const [fullContact, setFullContact] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeSection, setActiveSection] = useState('info');
  const [deleting, setDeleting] = useState(false);
  
  useEffect(() => {
    if (contact) {
      fetchContactDetails();
    }
  }, [contact?.id]);
  
  const fetchContactDetails = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/crm/contacts/${contact.id}/360`);
      setFullContact(res.data.contact);
      setTimeline(res.data.timeline || []);
      setTags(res.data.tags || []);
    } catch (error) {
      console.error('Error fetching contact details:', error);
      toast.error('Error al cargar el contacto');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar permanentemente a ${fullContact?.first_name || contact.email}? Esta acción no se puede deshacer.`)) {
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/crm/contacts/${contact.id}?hard=true`);
      toast.success('Contacto eliminado');
      onDelete?.(contact.id);
      onClose();
    } catch (error) {
      toast.error('Error al eliminar contacto');
    } finally {
      setDeleting(false);
    }
  };
  
  if (!contact) return null;
  
  const lifecycle = LIFECYCLE_CONFIG[fullContact?.lifecycle_stage || contact.lifecycle_stage] || LIFECYCLE_CONFIG.visitor;
  const temp = TEMPERATURE_CONFIG[fullContact?.lead_temperature || contact.lead_temperature] || TEMPERATURE_CONFIG.cold;
  const LifecycleIcon = lifecycle.icon;
  
  const interests = fullContact?.interests || {};
  const requests = fullContact?.requests || {};
  const summary = fullContact?.purchases || {};
  
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-50 overflow-y-auto" data-testid="contact-360-panel">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b z-10">
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${lifecycle.color}`}>
              <LifecycleIcon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">
                {fullContact?.first_name || contact.first_name || contact.email.split('@')[0]}
              </h2>
              <p className="text-sm text-gray-500">{contact.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleDelete}
              disabled={deleting}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              title="Eliminar contacto"
            >
              {deleting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="px-4 pb-3 flex gap-3">
          <Badge className={lifecycle.color}>{lifecycle.label}</Badge>
          <Badge variant="outline" className="gap-1">
            {temp.emoji} Score: {fullContact?.lead_score || contact.lead_score}
          </Badge>
          {summary.total_spent > 0 && (
            <Badge variant="outline" className="bg-green-50 text-green-700 gap-1">
              💰 {summary.total_spent}€
            </Badge>
          )}
        </div>
        
        {/* Section Tabs */}
        <div className="px-4 flex gap-1 overflow-x-auto pb-2">
          {['info', 'timeline', 'interests', 'tags'].map(section => (
            <Button
              key={section}
              variant={activeSection === section ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveSection(section)}
              className="capitalize"
            >
              {section === 'info' && 'Datos'}
              {section === 'timeline' && 'Timeline'}
              {section === 'interests' && 'Intereses'}
              {section === 'tags' && 'Tags'}
            </Button>
          ))}
        </div>
      </div>
      
      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : (
        <div className="p-4">
          {/* INFO SECTION */}
          {activeSection === 'info' && (
            <div className="space-y-6">
              {/* Contact Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Información de contacto</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{fullContact?.email}</span>
                  </div>
                  {fullContact?.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{fullContact.phone}</span>
                    </div>
                  )}
                  {fullContact?.whatsapp && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-green-500" />
                      <span>{fullContact.whatsapp}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Origin */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Origen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Fuente:</span>
                    <span className="font-medium">{fullContact?.source || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Detalle:</span>
                    <span className="font-medium">{fullContact?.source_detail || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">First touch:</span>
                    <span className="font-medium">
                      {fullContact?.first_touch_at 
                        ? new Date(fullContact.first_touch_at).toLocaleDateString('es-ES')
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Last touch:</span>
                    <span className="font-medium">
                      {fullContact?.last_touch_at 
                        ? new Date(fullContact.last_touch_at).toLocaleDateString('es-ES')
                        : 'N/A'}
                    </span>
                  </div>
                </CardContent>
              </Card>
              
              {/* Purchases */}
              {summary.total_purchases > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Compras</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Total compras:</span>
                      <span className="font-medium">{summary.total_purchases}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Total gastado:</span>
                      <span className="font-medium text-green-600">{summary.total_spent}€</span>
                    </div>
                    {summary.products?.length > 0 && (
                      <div className="pt-2">
                        <span className="text-sm text-gray-500">Productos:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {summary.products.map((p, i) => (
                            <Badge key={i} variant="secondary">{p}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Events Summary */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Resumen de actividad</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <div className="text-lg font-bold">{fullContact?.events_summary?.page_views || 0}</div>
                      <div className="text-xs text-gray-500">Visitas</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <div className="text-lg font-bold">{fullContact?.events_summary?.form_submits || 0}</div>
                      <div className="text-xs text-gray-500">Formularios</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <div className="text-lg font-bold">{fullContact?.events_summary?.ebook_downloads || 0}</div>
                      <div className="text-xs text-gray-500">Descargas</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* TIMELINE SECTION */}
          {activeSection === 'timeline' && (
            <div className="space-y-4">
              <h3 className="font-medium text-gray-700">Últimos eventos</h3>
              {timeline.length === 0 ? (
                <p className="text-sm text-gray-500">No hay eventos registrados</p>
              ) : (
                <div className="space-y-3">
                  {timeline.map((event, idx) => (
                    <div key={event.id || idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                        <Activity className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{event.event_name}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(event.timestamp).toLocaleString('es-ES', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        {event.properties && Object.keys(event.properties).length > 0 && (
                          <div className="mt-1 text-xs text-gray-400">
                            {JSON.stringify(event.properties).substring(0, 100)}...
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* INTERESTS SECTION */}
          {activeSection === 'interests' && (
            <div className="space-y-4">
              <h3 className="font-medium text-gray-700">Intereses y solicitudes</h3>
              
              {/* Interest flags */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Intereses</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(interests).map(([key, value]) => (
                      <Badge 
                        key={key} 
                        variant={value ? "default" : "outline"}
                        className={value ? "bg-green-100 text-green-700" : "text-gray-400"}
                      >
                        {value ? '✓' : '○'} {key}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* Active Requests */}
              {(requests.campus || requests.viajes || requests.limites || requests.publicaciones) && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-500">Solicitudes activas</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {requests.campus && (
                      <div className="p-2 bg-blue-50 rounded text-sm">
                        <strong>Campus:</strong> En lista de espera
                        <div className="text-xs text-gray-500">
                          {new Date(requests.campus.joined_at).toLocaleDateString('es-ES')}
                        </div>
                      </div>
                    )}
                    {requests.viajes && (
                      <div className="p-2 bg-teal-50 rounded text-sm">
                        <strong>Viaje:</strong> {requests.viajes.viaje}
                        {requests.viajes.mensaje && (
                          <div className="text-xs text-gray-600 mt-1">"{requests.viajes.mensaje}"</div>
                        )}
                      </div>
                    )}
                    {requests.limites && (
                      <div className="p-2 bg-pink-50 rounded text-sm">
                        <strong>Límites:</strong> Solicitud enviada
                        <div className="text-xs text-gray-500">
                          Estado: {requests.limites.status}
                        </div>
                      </div>
                    )}
                    {requests.publicaciones && (
                      <div className="p-2 bg-amber-50 rounded text-sm">
                        <strong>Publicaciones:</strong> En lista de espera
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          {/* TAGS SECTION */}
          {activeSection === 'tags' && (
            <div className="space-y-4">
              <h3 className="font-medium text-gray-700">Tags del contacto</h3>
              <div className="flex flex-wrap gap-2">
                {tags.length === 0 ? (
                  <p className="text-sm text-gray-500">Sin tags asignados</p>
                ) : (
                  tags.map(tag => (
                    <Badge 
                      key={tag.id} 
                      style={{ backgroundColor: tag.color + '20', color: tag.color, borderColor: tag.color }}
                      className="border"
                    >
                      {tag.name}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN CRM360 CONTACTS PAGE
// ============================================

const AdminCRM360Contacts = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Filters
  const [search, setSearch] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('');
  const [temperatureFilter, setTemperatureFilter] = useState('');
  const [interestFilter, setInterestFilter] = useState('');
  
  // Selected contact
  const [selectedContact, setSelectedContact] = useState(null);
  
  // Stats
  const [stats, setStats] = useState(null);
  
  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [page, lifecycleFilter, temperatureFilter, interestFilter]);
  
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '50' });
      if (search) params.append('search', search);
      if (lifecycleFilter) params.append('lifecycle_stage', lifecycleFilter);
      if (temperatureFilter) params.append('temperature', temperatureFilter);
      if (interestFilter) params.append('interest', interestFilter);
      
      const res = await api.get(`/crm/contacts?${params.toString()}`);
      setContacts(res.data.contacts);
      setTotal(res.data.total);
      setHasMore(res.data.has_more);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Error al cargar contactos');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const res = await api.get('/crm/dashboard');
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  
  const handleSearch = useCallback(() => {
    setPage(1);
    fetchContacts();
  }, [search]);
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="crm360-contacts-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Contactos CRM 360°</h1>
              <p className="text-sm text-gray-500">
                {total} contactos {lifecycleFilter && `• ${lifecycleFilter}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={fetchContacts}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Cards */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setLifecycleFilter('')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Total</p>
                    <p className="text-2xl font-bold">{stats.totals.total}</p>
                  </div>
                  <Users className="w-8 h-8 text-gray-300" />
                </div>
              </CardContent>
            </Card>
            
            {Object.entries(stats.by_lifecycle)
              .filter(([stage]) => LIFECYCLE_CONFIG[stage])
              .map(([stage, count]) => {
              const config = LIFECYCLE_CONFIG[stage];
              const Icon = config.icon;
              return (
                <Card 
                  key={stage} 
                  className={`cursor-pointer hover:shadow-md transition-shadow ${lifecycleFilter === stage ? 'ring-2 ring-amber-500' : ''}`}
                  onClick={() => setLifecycleFilter(lifecycleFilter === stage ? '' : stage)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">{config.label}</p>
                        <p className="text-2xl font-bold">{count}</p>
                      </div>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por email, nombre, teléfono..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          
          <select 
            value={temperatureFilter}
            onChange={(e) => setTemperatureFilter(e.target.value)}
            className="px-3 py-2 border rounded-md text-sm"
          >
            <option value="">Temperatura</option>
            <option value="hot">🔥 Caliente</option>
            <option value="warm">☀️ Tibio</option>
            <option value="cold">❄️ Frío</option>
          </select>
          
          <select 
            value={interestFilter}
            onChange={(e) => setInterestFilter(e.target.value)}
            className="px-3 py-2 border rounded-md text-sm"
          >
            <option value="">Interés</option>
            <option value="ebook">Ebook</option>
            <option value="facilitadoras">Facilitadoras</option>
            <option value="limites">Límites</option>
            <option value="campus">Campus</option>
            <option value="viajes">Viajes</option>
            <option value="publicaciones">Publicaciones</option>
          </select>
          
          {(lifecycleFilter || temperatureFilter || interestFilter) && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setLifecycleFilter('');
                setTemperatureFilter('');
                setInterestFilter('');
              }}
            >
              <X className="w-4 h-4 mr-1" />
              Limpiar filtros
            </Button>
          )}
        </div>
      </div>
      
      {/* Contacts Table */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading && contacts.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Users className="w-12 h-12 mb-2 text-gray-300" />
              <p>No se encontraron contactos</p>
            </div>
          ) : (
            <table className="w-full" data-testid="contacts-table">
              <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-3">Contacto</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Tags</th>
                  <th className="px-4 py-3">Fuente</th>
                  <th className="px-4 py-3">Últ. actividad</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {contacts.map(contact => (
                  <ContactRow 
                    key={contact.id}
                    contact={contact}
                    onClick={setSelectedContact}
                    isSelected={selectedContact?.id === contact.id}
                  />
                ))}
              </tbody>
            </table>
          )}
          
          {/* Pagination */}
          {total > 50 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <p className="text-sm text-gray-500">
                Mostrando {(page - 1) * 50 + 1} - {Math.min(page * 50, total)} de {total}
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Anterior
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={!hasMore}
                  onClick={() => setPage(p => p + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Contact Detail Panel */}
      {selectedContact && (
        <>
          <div 
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setSelectedContact(null)}
          />
          <ContactDetailPanel 
            contact={selectedContact}
            onClose={() => setSelectedContact(null)}
            onUpdate={fetchContacts}
            onDelete={(deletedId) => {
              setContacts(prev => prev.filter(c => c.id !== deletedId));
              setTotal(prev => prev - 1);
              fetchStats();
            }}
          />
        </>
      )}
    </div>
  );
};

export default AdminCRM360Contacts;
